import React, { useState } from 'react';
import { Box, Card, CardContent, Stack, Typography, Chip } from '@mui/material';
import RichText from './RichText';
import ItemIconV3 from '../v3/components/ItemIconV3';
import ItemViewDetails from '../v3/components/ItemViewDetails';
import { useTranslation } from 'react-i18next';
import { v3Theme } from '../v3/theme';
import { EQUIPMENT_STYLES } from '../v3/constants/equipmentConstants';

/**
 * UniversalInventory - универсальный компонент для отображения любых списков инвентаря
 * Используется для инвентаря игрока, склада гильдии, склада игрока и т.д.
 * 
 * @param {string} title - Заголовок (например "Inventory 50/100" или "Guild Stock 25/50")
 * @param {Array} inventory - Массив предметов [{name, icon, qty, code, category, actions}, ...]
 * @param {Array} filters - Массив доступных фильтров (например ['All', 'Resources', 'Equipment', ...])
 */
export default function UniversalInventory({ 
    title = 'Inventory', 
    inventory = [], 
    filters = ['All']
}) {
    const { t } = useTranslation();
    const [selectedFilter, setSelectedFilter] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    
    const safeInventory = Array.isArray(inventory) ? inventory : [];
    
    // Map for translating server filters to localized names
    const filterTranslations = {
        'Resources': t('widgets.inventory.filters.resources'),
        'Equipment': t('widgets.inventory.filters.equipment'),
        'Consumables': t('widgets.inventory.filters.consumables'),
        'Craft': t('widgets.inventory.filters.craft'),
        'Food': t('widgets.inventory.filters.food'),
        'Misc': t('widgets.inventory.filters.misc')
    };
    
    // Translate server filters to localized names
    const localizedFilters = filters.map(filter => filterTranslations[filter] || filter);
    
    // Фильтрация предметов
    const displayedItems = !selectedFilter
        ? safeInventory
        : safeInventory.filter((item) => {
            // Get the server category name from selected localized filter
            const serverFilter = Object.keys(filterTranslations).find(
                key => filterTranslations[key] === selectedFilter
            );
            return item.category === serverFilter;
        });
    
    const handleFilterClick = (filterName) => {
        setSelectedFilter((prev) => (prev === filterName ? null : filterName));
    };
    
    return (
        <>
            <Card sx={{ mb: 2 }}>
                <CardContent sx={{ 
                    px: { xs: 2, sm: 2, md: 2 }, // Keep horizontal padding as is
                    py: { xs: 2, sm: 4, md: 6 },  // Double vertical padding
                    fontFamily: EQUIPMENT_STYLES.FONT_FAMILY_QUANTITY // Apply JetBrains Mono font
                }}>
                    <Typography variant="body1" component="div" sx={{ 
                        mb: 2, 
                        color: v3Theme.main,
                        fontFamily: EQUIPMENT_STYLES.FONT_FAMILY_QUANTITY
                    }}>
                        {title}
                    </Typography>
                    
                    {/* Фильтры */}
                    {localizedFilters && localizedFilters.length > 1 && (
                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{
                                mb: 2,
                                flexWrap: 'wrap',
                                rowGap: 1,
                            }}
                        >
                            {localizedFilters.map((filterName) => (
                                <Chip
                                    key={filterName}
                                    label={filterName}
                                    clickable
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleFilterClick(filterName);
                                    }}
                                    sx={{
                                        backgroundColor: selectedFilter === filterName ? v3Theme.main : undefined,
                                        color: selectedFilter === filterName ? v3Theme.text.contrast : undefined,
                                        fontFamily: EQUIPMENT_STYLES.FONT_FAMILY_QUANTITY,
                                        '&:hover': {
                                            backgroundColor: selectedFilter === filterName ? v3Theme.main : undefined,
                                        }
                                    }}
                                />
                            ))}
                        </Stack>
                    )}
                    
                    {/* Список предметов */}
                    {displayedItems.length > 0 ? (
                        displayedItems.map((item, index) => (
                            <Stack
                                key={`${item.code}-${index}`}
                                direction="row"
                                spacing={2}
                                alignItems="center"
                                sx={{ mb: 1, cursor: 'pointer' }}
                                onClick={() => {
                                    // Open dialog immediately for better UX
                                    setSelectedItem(item);
                                }}
                            >
                                <Box sx={{
                                    width: 24,
                                    height: 24,
                                    flexShrink: 0,
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                    // Safari fixes
                                    transform: 'translateZ(0)',
                                    WebkitTransform: 'translateZ(0)',
                                    backfaceVisibility: 'hidden',
                                    WebkitBackfaceVisibility: 'hidden',
                                    '& img': {
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'contain',
                                        borderRadius: 1,
                                        // Safari image rendering fixes - use auto for better quality
                                        imageRendering: 'auto',
                                        WebkitFontSmoothing: 'subpixel-antialiased',
                                    }
                                }}>
                                    <ItemIconV3
                                        icon={item.icon || 'unknown'}
                                        alt={item.name}
                                    />
                                </Box>
                                
                                <Typography variant="body2" sx={{ 
                                    flexGrow: 1, 
                                    color: v3Theme.main,
                                    fontFamily: EQUIPMENT_STYLES.FONT_FAMILY_QUANTITY
                                }}>
                                    <RichText content={item.name || ''} />
                                </Typography>
                                
                                <Typography
                                    variant="body2"
                                    sx={{ 
                                        fontWeight: 'medium', 
                                        color: v3Theme.main,
                                        fontFamily: EQUIPMENT_STYLES.FONT_FAMILY_QUANTITY
                                    }}
                                >
                                    {item.qty}
                                </Typography>
                            </Stack>
                        ))
                    ) : (
                        <Typography variant="body2" sx={{
                            color: 'text.secondary',
                            fontFamily: EQUIPMENT_STYLES.FONT_FAMILY_QUANTITY
                        }}>
                            {selectedFilter && selectedFilter !== 'All' 
                                ? t('widgets.inventory.noItemsInCategory')
                                : t('widgets.inventory.noItems')}
                        </Typography>
                    )}
                </CardContent>
            </Card>
            
            {/* Детали предмета */}
            <ItemViewDetails
                item={selectedItem}
                open={!!selectedItem}
                onClose={() => {
                    setSelectedItem(null);
                }}
            />
        </>
    );
}